import React, { useEffect, useRef, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { getDashboardAnalytics } from "../../apiServices/AnalyticsServices";

const CACHE_KEY = "dashboard_cards_cache";
const CACHE_TIME = 3 * 60 * 1000;

const UserPanel = ({ dashboardData }) => {
  const authuser = JSON.parse(localStorage.getItem("authUser"));

  const token = authuser?.token;
  const userId = authuser?.response?._id;

  const [cards, setCards] = useState([]);

  const animationRefs = useRef({});

  const animateValue = (id, start, end) => {
    if (start === end) return end;

    if (animationRefs.current[id]) {
      cancelAnimationFrame(animationRefs.current[id]);
    }

    const duration = 700;
    const startTime = performance.now();

    const update = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);

      const value = Math.round(start + (end - start) * progress);

      setCards((prev) =>
        prev.map((c) =>
          c.title === id
            ? {
                ...c,
                value,
              }
            : c
        )
      );

      if (progress < 1) {
        animationRefs.current[id] = requestAnimationFrame(update);
      }
    };

    animationRefs.current[id] = requestAnimationFrame(update);
  };

  const fetchDashboardData = async () => {
    try {
      const response = await getDashboardAnalytics();

      const latestCards = response?.data?.cards || [];

      setCards((prevCards) => {
        if (prevCards.length === 0) {
          return latestCards;
        }

        latestCards.forEach((newCard) => {
          const oldCard = prevCards.find((c) => c.title === newCard.title);

          if (!oldCard) return;

          if (Number(oldCard.value) !== Number(newCard.value)) {
            animateValue(
              newCard.title,
              Number(oldCard.value),
              Number(newCard.value)
            );
          }
        });

        return prevCards.map((oldCard) => {
          const latest = latestCards.find((x) => x.title === oldCard.title);

          if (!latest) return oldCard;

          return {
            ...latest,
            value: oldCard.value,
          };
        });
      });

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          time: Date.now(),
          data: latestCards,
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (dashboardData?.cards?.length) {
      setCards(dashboardData.cards);
    } else {
      const cache = localStorage.getItem(CACHE_KEY);

      if (cache) {
        try {
          const parsed = JSON.parse(cache);

          if (Date.now() - parsed.time < CACHE_TIME) {
            setCards(parsed.data || []);
          }
        } catch {}
      }

      fetchDashboardData();
    }
  }, [dashboardData, token, userId]);

  return (
    <React.Fragment>
      <Row className="g-3">
        {cards.map((card, index) => (
          <Col xl={3} lg={3} md={4} sm={6} xs={6} key={index}>
            {card.redirect && card.redirect !== "*" ? (
              <Link
                to={card.redirect}
                className="text-decoration-none text-reset"
              >
                <Card
                  className={`shadow-sm border-0 dashboard-card dashboard-${card.color}`}
                >
                  <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="text-muted mb-1 small fw-semibold">
                          {card.title}
                        </p>

                        <h3 className="fw-bold mb-1">
                          {Number(card.value).toLocaleString()}
                        </h3>

                        <small className={`text-${card.color}`}>
                          Live Analytics
                        </small>
                      </div>

                      <div
                        className={`dashboard-icon bg-soft-${card.color} text-${card.color}`}
                      >
                        <i className={`${card.icon} fs-2`} />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Link>
            ) : (
              <Card
                className={`shadow-sm border-0 dashboard-card dashboard-${card.color}`}
              >
                <CardBody className="p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small fw-semibold">
                        {card.title}
                      </p>

                      <h3 className="fw-bold mb-1">
                        {Number(card.value).toLocaleString()}
                      </h3>

                      <small className={`text-${card.color}`}>
                        Live Analytics
                      </small>
                    </div>

                    <div
                      className={`dashboard-icon bg-soft-${card.color} text-${card.color}`}
                    >
                      <i className={`${card.icon} fs-2`} />
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>
        ))}
      </Row>
    </React.Fragment>
  );
};

export default UserPanel;